// Copyright (c) Mysten Labs, Inc. — DGC header
// SPDX-License-Identifier: Apache-2.0

import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { AsideBottomPanel } from "./AsideBottomPanel";
import { LinkWithQuery } from "~/ui/utils/LinkWithQuery";
import Search from "../search/Search";
import { useT } from "~/i18n/LocaleContext";

import {
	Bars3Icon,
	XMarkIcon,
} from "@heroicons/react/24/outline";

function ExplorerLogo({ className }: { className: string }) {
	return (
		<img src="/dgc.png" alt="DGC" className={className} />
	);
}

function Header() {
	const [isScrolled, setIsScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const t = useT();

	const location = useLocation();

	useEffect(() => {
		const callback = () => {
			setIsScrolled(window.scrollY > 0);
		};

		callback();

		document.addEventListener("scroll", callback, {
			passive: true,
		});

		return () => {
			document.removeEventListener("scroll", callback);
		};
	}, []);

	const isActive = (path: string) =>
		path === '/bridge'
			? location.pathname === '/bridge' || location.pathname.startsWith('/bridge/')
			: location.pathname === path;

	const closeMenu = () => {
		setMobileMenuOpen(false);
	};

	const navLinkClass = (path: string, mobile = false) =>
		clsx(
			mobile
				? "flex items-center h-11 px-4 rounded-xl font-semibold text-sm"
				: "flex items-center h-11 px-4 rounded-xl font-semibold transition-all duration-200",
			isActive(path)
				? "bg-[#38BDF8]/10 text-[#38BDF8]"
				: mobile
					? "text-[#AEB9D0]"
					: "text-[#AEB9D0] hover:bg-[#1E293B] hover:text-white",
		);

	const navItems = [
		{ to: "/trade", label: t("nav.dex") },
		{ to: "/bridge", label: t("nav.bridge") },
		{ to: "/stake", label: t("nav.stake") },
		{ to: "/genesis", label: t("nav.genesis") },
		{ to: "/wallet", label: t("nav.apps") },
	] as const;

	return (
		<div>
			{/* ==================== 1. PC 端侧边导航栏 (左侧静止固定) ==================== */}
			<aside className="hidden md:flex fixed inset-y-0 left-0 z-50 w-64 flex-col justify-between border-r border-[#1E293B] bg-[#0F172A] p-5 print:hidden">
				<div className="flex flex-col gap-8">
					{/* Logo 区域 */}
					<LinkWithQuery
						data-testid="nav-logo-button"
						to="/"
						className="flex items-center gap-2.5 text-[#38BDF8] font-bold text-lg px-2"
					>
						<ExplorerLogo className="h-7 w-7 rounded-full object-cover" />
						<span>{t("explorer.title")}</span>
					</LinkWithQuery>

					{/* 导航链接列表 */}
					<nav className="flex flex-col gap-2">
						{navItems.map((item) => (
							<Link key={item.to} to={item.to} className={navLinkClass(item.to)}>
								{item.label}
							</Link>
						))}
					</nav>
				</div>

				{/* PC 底部：网络 + 钱包 */}
				<AsideBottomPanel />
			</aside>

			{/* ==================== 2. PC 端右侧顶部横条 (专门承载右侧搜索框) ==================== */}
			<div
				className={clsx(
					"hidden md:flex fixed top-0 right-0 left-64 z-40 h-16 items-center justify-end px-8 bg-[#0F172A]/80 backdrop-blur-md border-b border-[#1E293B] transition-shadow print:hidden",
					isScrolled && "shadow-lg shadow-[#000]/20"
				)}
			>
				<div className="w-full">
					<Search />
				</div>
			</div>


			{/* ==================== 3. H5 移动端：顶部控制通栏 ==================== */}
			<header
				className={clsx(
					"sticky top-0 z-50 flex h-16 w-full items-center justify-between border-b border-[#1E293B] bg-[#0F172A]/95 px-4 backdrop-blur-xl md:hidden print:hidden",
					isScrolled && "shadow-effect-ui-regular"
				)}
			>
				{/* 移动端 Logo */}
				<LinkWithQuery
					data-testid="nav-logo-button"
					to="/"
					className="flex items-center gap-2 text-[#38BDF8] font-bold"
				>
					<ExplorerLogo className="h-6 w-6 rounded-full object-cover" />
					<span>DGC</span>
				</LinkWithQuery>
				<div className="w-full ml-2">
					<Search />
				</div>
				{/* 右侧触发菜单按钮 */}
				<button
					onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					className="p-2 rounded-lg text-[#AEB9D0] hover:bg-[#1E293B] focus:outline-none touch-manipulation"
				>
					{mobileMenuOpen ? (
						<XMarkIcon className="h-6 w-6" />
					) : (
						<Bars3Icon className="h-6 w-6" />
					)}
				</button>
			</header>

			{/* ==================== 4. H5 移动端：高级优雅侧滑抽屉菜单 ==================== */}
			<div>
				{/* 遮罩背景层 */}
				<div
					onClick={closeMenu}
					className={clsx(
						"md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300",
						mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
					)}
				/>

				{/* 侧滑抽屉主体 */}
				<div
					className={clsx(
						"md:hidden fixed inset-y-0 left-0 z-50 w-[280px] bg-[#111827] border-r border-[#1E293B] p-5 flex flex-col justify-between transition-transform duration-300 ease-out",
						mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
					)}
				>
					<div className="flex flex-col gap-6">
						{/* 抽屉顶部头部 */}
						<div className="flex items-center justify-between border-b border-[#1E293B] pb-4">
							<div className="flex items-center gap-2 text-[#38BDF8] font-bold">
								<ExplorerLogo className="h-6 w-6 rounded-full object-cover" />
								<span>{t("explorer.title")}</span>
							</div>
						</div>

						{/* 移动端菜单列表 */}
						<nav className="flex flex-col gap-1.5">
							{navItems.map((item) => (
								<Link
									key={item.to}
									to={item.to}
									onClick={closeMenu}
									className={navLinkClass(item.to, true)}
								>
									{item.label}
								</Link>
							))}
						</nav>
					</div>

					{/* 抽屉底部：网络 + 钱包 */}
					<AsideBottomPanel compact />
				</div>
			</div>
		</div>
	);
}

export default Header;